/**
 * Column widths a reviewer set by dragging, remembered per platform.
 *
 * Keyed on platform and level rather than on the Job: the columns are the
 * platform's own fields, so the layout someone settles on is a preference about
 * reading Meta ad sets, not about one spreadsheet. Keying it per Job would make
 * every upload start from scratch, which is the opposite of what dragging a
 * column is for.
 *
 * Local to the browser on purpose. This is a view preference with no meaning to
 * anyone else, and a round trip to store it would be a worse trade than losing it
 * when someone switches machines.
 */
const STORAGE_KEY = 'delivery.columnWidths';

/** Below this a text column's header label is unreadable. */
export const MIN_COLUMN_WIDTH = 80;

/**
 * The floor for the icon-only columns, which ship narrower than a text column's
 * minimum — the issue badge is 52px and the preview button 76px, so a single
 * global floor would silently widen them the first time a width was read back.
 */
export const MIN_NARROW_COLUMN_WIDTH = 40;

/** Wide enough that a runaway drag cannot push every other column off-screen. */
export const MAX_COLUMN_WIDTH = 640;

export type ColumnWidths = Record<string, number>;

export function columnWidthKey(platform: string, level: string, fieldKey: string): string {
  return `${platform}.${level}.${fieldKey}`;
}

export function clampColumnWidth(width: number, min: number = MIN_COLUMN_WIDTH): number {
  return Math.min(Math.max(Math.round(width), min), MAX_COLUMN_WIDTH);
}

/**
 * Anything unreadable is treated as no preference at all.
 *
 * Storage is shared with the rest of the app and survives deploys, so it can hold
 * a shape this build no longer understands. A stale entry must not take the table
 * down with it, and a column with no remembered width already has a sane default.
 */
export function readColumnWidths(): ColumnWidths {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};

    // Clamped against the narrow floor, not the text one: this cannot tell which
    // column a key belongs to, and using the wider floor here would inflate every
    // stored icon column on the next read.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter(([, width]) => typeof width === 'number' && Number.isFinite(width))
        .map(([key, width]) => [key, clampColumnWidth(width as number, MIN_NARROW_COLUMN_WIDTH)]),
    );
  } catch {
    return {};
  }
}

/** Silently a no-op when storage is unavailable: a lost width is not worth an error. */
export function writeColumnWidths(widths: ColumnWidths): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {
    /* private mode, or the quota is full */
  }
}
